import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Search, Loader2, CheckCircle } from 'lucide-react';
import { useToaster } from '../../contexts/ToasterContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '../../components/ui/breadcrumb';

// Shimmer Loading Component
const ShimmerCard = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`}></div>
);

const ShimmerText = ({ width = "w-3/4", height = "h-4" }) => (
  <ShimmerCard className={`${width} ${height} mb-2`} />
);

const RepositoryShimmer = () => (
  <div className="flex items-center gap-3 p-4">
    <ShimmerCard className="w-4 h-4 rounded" />
    <div className="flex-1 min-w-0">
      <ShimmerText width="w-3/4" height="h-4" />
    </div>
  </div>
);

const BranchShimmer = () => (
  <div className="flex items-center gap-2 p-2">
    <ShimmerCard className="w-4 h-4 rounded" />
    <ShimmerText width="w-1/4" height="h-3" />
  </div>
);

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToaster();
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  
  // Step 1 - Repository Selection
  const [repositories, setRepositories] = useState([]);
  const [selectedRepos, setSelectedRepos] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingRepos, setIsLoadingRepos] = useState(true);
  
  // Normal mode pagination
  const [normalPage, setNormalPage] = useState(1);
  const [normalPerPage, setNormalPerPage] = useState(50);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 50,
    last_page: 1,
    has_next: false,
    has_previous: false
  });
  
  // Search mode pagination
  const [searchPage, setSearchPage] = useState(1);
  const [searchPerPage, setSearchPerPage] = useState(30);
  const [searchPagination, setSearchPagination] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  
  // Step 2 - Branch Selection
  const [branches, setBranches] = useState(new Map()); // repoId -> { branches: [], loading: boolean, expanded: boolean }
  const [selectedBranches, setSelectedBranches] = useState(new Map()); // repoId -> [branchNames]
  const [activeRepoId, setActiveRepoId] = useState(null);
  const [branchPagination, setBranchPagination] = useState(new Map()); // repoId -> pagination info
  const [branchPage, setBranchPage] = useState(new Map()); // repoId -> current page
  const [branchPerPage, setBranchPerPage] = useState(new Map()); // repoId -> per_page
  const [emptyRepositories, setEmptyRepositories] = useState(new Set()); // Track repos with 0 branches
  const [branchSearchQuery, setBranchSearchQuery] = useState('');
  const [branchSearchResults, setBranchSearchResults] = useState(new Map()); // repoId -> search results
  const [isBranchSearchLoading, setIsBranchSearchLoading] = useState(false);
  const [isBranchSearchMode, setIsBranchSearchMode] = useState(new Map()); // repoId -> boolean
  const [branchSearchPagination, setBranchSearchPagination] = useState(new Map()); // repoId -> pagination info
  const [branchSearchPage, setBranchSearchPage] = useState(new Map()); // repoId -> current page
  const [branchSearchPerPage, setBranchSearchPerPage] = useState(new Map()); // repoId -> per_page
  
  // Step 3 - Schedule Configuration
  const [repeatInterval, setRepeatInterval] = useState('1h');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [startTime, setStartTime] = useState('now');
  const [showScheduleDropdown, setShowScheduleDropdown] = useState(false);
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false);
  const [customTime, setCustomTime] = useState('1h');
  const [isCreatingSchedules, setIsCreatingSchedules] = useState(false);

  // Mock data for non-onboarded repositories
  const getMockRepositories = (page = 1, per_page = 50) => {
    const mockRepos = Array.from({ length: 150 }, (_, i) => ({
      repository_id: `repo-${i + 1}`,
      name: `repository-${i + 1}`,
      owner: `owner-${(i % 10) + 1}`,
      full_name: `owner-${(i % 10) + 1}/repository-${i + 1}`,
      description: `Mock repository description ${i + 1}`,
      provider: 'github'
    }));

    const start = (page - 1) * per_page;
    const end = start + per_page;
    const paginatedRepos = mockRepos.slice(start, end);
    const totalCount = mockRepos.length;
    const lastPage = Math.ceil(totalCount / per_page);

    return {
      data: paginatedRepos,
      pagination: {
        page,
        per_page,
        last_page: lastPage,
        has_next: page < lastPage,
        has_previous: page > 1,
        total_count: totalCount
      }
    };
  };

  // Fetch non-onboarded repositories
  const fetchRepositories = async (page = normalPage, per_page = normalPerPage) => {
    setIsLoadingRepos(true);
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const mockResponse = getMockRepositories(page, per_page);
      
      setRepositories(mockResponse.data || []);
      setNormalPage(page);
      setNormalPerPage(per_page);
      setPagination(mockResponse.pagination);
    } catch (error) {
      console.error('Error fetching repositories:', error);
      showToast('Failed to fetch repositories', 'error');
    } finally {
      setIsLoadingRepos(false);
    }
  };

  // Mock search results
  const getMockSearchResults = (query, page = 1, per_page = 30) => {
    const mockResults = Array.from({ length: 75 }, (_, i) => ({
      repository_id: `search-repo-${i + 1}`,
      id: `search-repo-${i + 1}`,
      name: `${query}-repo-${i + 1}`,
      owner: `search-owner-${(i % 5) + 1}`,
      full_name: `search-owner-${(i % 5) + 1}/${query}-repo-${i + 1}`,
      description: `Mock search result for "${query}" - ${i + 1}`,
      provider: 'github'
    }));

    const start = (page - 1) * per_page;
    const end = start + per_page;
    const paginatedResults = mockResults.slice(start, end);
    const totalCount = mockResults.length;
    const lastPage = Math.ceil(totalCount / per_page);

    return {
      data: paginatedResults,
      pagination: {
        page,
        per_page,
        last_page: lastPage,
        has_next: page < lastPage,
        has_previous: page > 1,
        total_count: totalCount
      }
    };
  };

  // Search GitHub repositories globally
  const searchGitHubRepositories = async (query, page = 1, per_page = 30) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setSearchPagination({
        page: 1,
        per_page: 30,
        last_page: 1,
        has_next: false,
        has_previous: false
      });
      return;
    }

    setIsSearchLoading(true);
    setSearchError('');
    
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 400));
      
      const mockResponse = getMockSearchResults(query, page, per_page);
      
      const normalizedSearchResults = mockResponse.data.map((r) => ({
        id: r.repository_id || r.id,
        repository_id: r.repository_id,
        name: r.name,
        owner: r.owner,
        full_name: r.full_name || r.name,
        description: r.description || '',
        provider: r.provider
      }));
      setSearchResults(normalizedSearchResults);
      setSearchPagination(mockResponse.pagination);
    } catch (error) {
      console.error('Error searching GitHub repositories:', error);
      const backendMessage = 'Failed to search GitHub repositories';
      setSearchError(backendMessage);
      showToast(backendMessage, 'error');
    } finally {
      setIsSearchLoading(false);
    }
  };

  // Mock branches data
  const getMockBranches = (repo, page = 1, per_page = 100) => {
    const branchNames = ['main', 'master', 'develop', 'staging', 'production', 'dev', 'test', 'feature/new-ui', 'feature/api', 'hotfix/bug-fix'];
    const allBranches = Array.from({ length: 45 }, (_, i) => {
      if (i < branchNames.length) {
        return { name: branchNames[i], protected: i < 3 };
      }
      return { name: `branch-${i + 1}`, protected: false };
    });

    const start = (page - 1) * per_page;
    const end = start + per_page;
    const paginatedBranches = allBranches.slice(start, end);
    const totalCount = allBranches.length;
    const lastPage = Math.ceil(totalCount / per_page);

    return {
      data: paginatedBranches,
      pagination: {
        page,
        per_page,
        last_page: lastPage,
        has_next: page < lastPage,
        has_previous: page > 1,
        total_count: totalCount
      }
    };
  };

  // Fetch branches for a repository
  const fetchBranches = async (repo, page = 1, per_page = 100) => {
    const repoId = repo.repository_id;
    
    // Mark as loading
    setBranches(prev => new Map(prev).set(repoId, {
      branches: [],
      loading: true,
      expanded: prev.get(repoId)?.expanded || false
    }));

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const mockResponse = getMockBranches(repo, page, per_page);
      const branchList = mockResponse.data;
      const paginationInfo = mockResponse.pagination;
      
      setBranchPagination(prev => new Map(prev).set(repoId, paginationInfo));
      
      // Auto-select main/master if available (only on first page)
      let selectedBranchNames = [];
      if (page === 1) {
        const defaultBranch = branchList.find(b => 
          b.name === 'main' || b.name === 'master'
        );
        selectedBranchNames = defaultBranch ? [defaultBranch.name] : [];
        
        // Only set selected branches if this is the first page and no branches are already selected
        const existingSelectedBranches = selectedBranches.get(repoId) || [];
        if (existingSelectedBranches.length === 0) {
          setSelectedBranches(prev => new Map(prev).set(repoId, selectedBranchNames));
        } else {
          selectedBranchNames = existingSelectedBranches;
        }
      } else {
        // Keep existing selected branches for subsequent pages
        selectedBranchNames = selectedBranches.get(repoId) || [];
      }
      
      setBranches(prev => new Map(prev).set(repoId, {
        branches: branchList,
        loading: false,
        expanded: prev.get(repoId)?.expanded || false
      }));
      
      // Track empty repositories
      setEmptyRepositories(prev => {
        const newSet = new Set(prev);
        if (branchList.length === 0) {
          newSet.add(repoId);
        } else {
          newSet.delete(repoId);
        }
        return newSet;
      });
      
    } catch (error) {
      console.error('Error fetching branches:', error);
      showToast(`Failed to fetch branches for ${repo.full_name}`, 'error');
      
      setBranches(prev => new Map(prev).set(repoId, {
        branches: [],
        loading: false,
        expanded: prev.get(repoId)?.expanded || false
      }));
      
      // Mark as empty repository on error
      setEmptyRepositories(prev => new Set(prev).add(repoId));
    }
  };

  // Mock branch search results
  const getMockBranchSearchResults = (query, page = 1, per_page = 100) => {
    const allBranches = Array.from({ length: 30 }, (_, i) => ({
      name: `${query}-branch-${i + 1}`,
      protected: i < 3
    }));

    const start = (page - 1) * per_page;
    const end = start + per_page;
    const paginatedBranches = allBranches.slice(start, end);
    const totalCount = allBranches.length;
    const lastPage = Math.ceil(totalCount / per_page);

    return {
      data: paginatedBranches,
      pagination: {
        page,
        per_page,
        last_page: lastPage,
        has_next: page < lastPage,
        has_previous: page > 1,
        total_count: totalCount
      }
    };
  };

  // Search branches for a repository
  const searchBranches = async (repo, query, page = 1, per_page = 100) => {
    if (!query || query.trim().length < 2) {
      // Clear search results and exit search mode
      const repoId = repo.repository_id;
      setIsBranchSearchMode(prev => {
        const newMap = new Map(prev);
        newMap.set(repoId, false);
        return newMap;
      });
      setBranchSearchResults(prev => {
        const newMap = new Map(prev);
        newMap.delete(repoId);
        return newMap;
      });
      setBranchSearchPagination(prev => {
        const newMap = new Map(prev);
        newMap.delete(repoId);
        return newMap;
      });
      return;
    }

    const repoId = repo.repository_id;
    setIsBranchSearchLoading(true);
    
    // Enter search mode
    setIsBranchSearchMode(prev => {
      const newMap = new Map(prev);
      newMap.set(repoId, true);
      return newMap;
    });

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const mockResponse = getMockBranchSearchResults(query, page, per_page);
      const branchList = mockResponse.data;
      const paginationInfo = mockResponse.pagination;
      
      // Store search results
      setBranchSearchResults(prev => {
        const newMap = new Map(prev);
        newMap.set(repoId, branchList);
        return newMap;
      });
      
      // Store pagination info
      setBranchSearchPagination(prev => {
        const newMap = new Map(prev);
        newMap.set(repoId, paginationInfo);
        return newMap;
      });
      
    } catch (error) {
      console.error('Error searching branches:', error);
      const backendMessage = 'Failed to search branches';
      showToast(backendMessage, 'error');
      
      // Clear search results on error
      setBranchSearchResults(prev => {
        const newMap = new Map(prev);
        newMap.delete(repoId);
        return newMap;
      });
      setBranchSearchPagination(prev => {
        const newMap = new Map(prev);
        newMap.delete(repoId);
        return newMap;
      });
    } finally {
      setIsBranchSearchLoading(false);
    }
  };

  // Initialize repositories on component mount
  useEffect(() => {
    fetchRepositories();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Branch search effect with debouncing
  useEffect(() => {
    if (!activeRepoId) {
      return;
    }

    // If search query is empty, exit search mode
    if (!branchSearchQuery || branchSearchQuery.trim().length === 0) {
      setIsBranchSearchMode(prev => {
        const newMap = new Map(prev);
        newMap.set(activeRepoId, false);
        return newMap;
      });
      setBranchSearchResults(prev => {
        const newMap = new Map(prev);
        newMap.set(activeRepoId, []);
        return newMap;
      });
      setBranchSearchPagination(prev => {
        const newMap = new Map(prev);
        newMap.delete(activeRepoId);
        return newMap;
      });
      return;
    }

    const repo = selectedRepos.find(r => r.repository_id === activeRepoId);
    if (!repo) return;

    // Get current page and per_page for this repo
    const page = branchSearchPage.get(activeRepoId) || 1;
    const per_page = branchSearchPerPage.get(activeRepoId) || 100;

    const timeout = setTimeout(() => {
      searchBranches(repo, branchSearchQuery, page, per_page);
    }, 400); // 400ms debounce

    return () => {
      clearTimeout(timeout);
    };
  }, [branchSearchQuery, activeRepoId, branchSearchPage, branchSearchPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // GitHub search function
  const performSearch = async (query, page, perPage) => {
    setIsSearchLoading(true);
    setSearchError('');

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 400));
      
      const mockResponse = getMockSearchResults(query, page, perPage);
      const normalized = mockResponse.data.map((r) => ({
        id: r.repository_id || r.id,
        repository_id: r.repository_id,
        name: r.name,
        owner: r.owner,
        full_name: r.full_name || r.name,
        description: r.description || '',
        provider: r.provider
      }));
      setSearchResults(normalized);
      setSearchPagination(mockResponse.pagination);
    } catch (err) {
      const backendMessage = 'Failed to search repositories';
      setSearchError(backendMessage);
    } finally {
      setIsSearchLoading(false);
    }
  };

  // GitHub search effect
  useEffect(() => {
    const query = searchQuery.trim();

    // Reset states when input is cleared
    if (query.length === 0) {
      setSearchResults([]);
      setIsSearchLoading(false);
      setSearchError('');
      setSearchPagination(null);
      setIsSearchMode(false);
      return;
    }

    // Only hit API for meaningful queries
    if (query.length < 2) {
      setSearchResults([]);
      setIsSearchLoading(false);
      setSearchError('');
      setSearchPage(1);
      setIsSearchMode(false);
      return;
    }

    // Enter search mode if not already in it
    if (!isSearchMode) {
      setIsSearchMode(true);
      setSearchPage(1);
    }

    // Perform search with debouncing for query changes, immediate for pagination changes
    const isQueryChange = !isSearchMode;
    const timeout = setTimeout(() => {
      performSearch(query, searchPage, searchPerPage);
    }, isQueryChange ? 400 : 0);

    return () => {
      clearTimeout(timeout);
    };
  }, [searchQuery, searchPage, searchPerPage, isSearchMode]);

  // Get the repositories to display (search results or normal repositories)
  const displayedRepositories = useMemo(() => {
    const reposToDisplay = isSearchMode ? searchResults : repositories;
    
    // Sort repositories to move selected ones to the top
    return [...reposToDisplay].sort((a, b) => {
      const aSelected = selectedRepos.some((r) => r.repository_id === a.repository_id);
      const bSelected = selectedRepos.some((r) => r.repository_id === b.repository_id);
      
      // If one is selected and the other isn't, selected comes first
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      
      // If both are selected or both are unselected, maintain original order
      return 0;
    });
  }, [isSearchMode, searchResults, repositories, selectedRepos]);
  // Handle search query changes with debouncing
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSearchError('');
      setIsSearchLoading(false);
      return;
    }

    if (searchQuery.trim().length < 2) {
      return;
    }

    let cancelled = false;
    setIsSearchLoading(true);
    setSearchError('');
    
    const timeout = setTimeout(async () => {
      if (!cancelled) {
        await searchGitHubRepositories(searchQuery.trim());
      }
    }, 400); // 400ms debounce
    
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter repositories based on search query
  // const filteredRepositories = useMemo(() => {
  //   if (!searchQuery.trim()) return repositories;
    
  //   // If search query is >= 2 characters, show search results instead of local filtering
  //   if (searchQuery.trim().length >= 2) {
  //     return searchResults;
  //   }
    
  //   // For queries < 2 characters, do local filtering
  //   return repositories.filter(repo =>
  //     repo.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //     repo.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //     repo.owner?.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //     repo.description?.toLowerCase().includes(searchQuery.toLowerCase())
  //   );
  // }, [repositories, searchQuery, searchResults]);

  // Repository selection handlers
  const handleSelectRepo = (repo) => {
    // Store current scroll position before updating
    const repoContainer = document.querySelector('.max-h-96.overflow-y-auto.border.rounded-md');
    const scrollTop = repoContainer?.scrollTop || 0;
    
    setSelectedRepos(prev => {
      const isSelected = prev.some(r => r.repository_id === repo.repository_id);
      return isSelected
        ? prev.filter(r => r.repository_id !== repo.repository_id)
        : [...prev, repo];
    });
    
    // Restore scroll position after state update
    setTimeout(() => {
      if (repoContainer) {
        repoContainer.scrollTop = scrollTop;
      }
    }, 0);
  };

  // Branch selection handlers
  const handleSelectBranch = (repoId, branchName) => {
    // Store current scroll position before updating
    const branchContainer = document.querySelector('.branch-list-container');
    const scrollTop = branchContainer?.scrollTop || 0;
    
    setSelectedBranches(prev => {
      const newMap = new Map(prev);
      const currentBranches = newMap.get(repoId) || [];
      
      if (currentBranches.includes(branchName)) {
        const updatedBranches = currentBranches.filter(b => b !== branchName);
        newMap.set(repoId, updatedBranches);
      } else {
        const updatedBranches = [...currentBranches, branchName];
        newMap.set(repoId, updatedBranches);
      }
      
      return newMap;
    });
    
    // Restore scroll position after state update
    setTimeout(() => {
      if (branchContainer) {
        branchContainer.scrollTop = scrollTop;
      }
    }, 0);
  };

  const toggleRepoExpansion = (repoId) => {
    // Handle summary accordion
    if (repoId === 'summary') {
      setBranches(prev => {
        const newMap = new Map(prev);
        const current = newMap.get('summary') || { expanded: false };
        newMap.set('summary', { ...current, expanded: !current.expanded });
        return newMap;
      });
      return;
    }
    
    // Handle repository expansion
    const repo = selectedRepos.find(r => r.repository_id === repoId);
    if (!repo) return;
    
    setBranches(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(repoId) || { branches: [], loading: false, expanded: false };
      newMap.set(repoId, { ...current, expanded: !current.expanded });
      return newMap;
    });
    
    // Fetch branches if not already loaded
    const currentBranches = branches.get(repoId);
    if (!currentBranches || currentBranches.branches.length === 0) {
      fetchBranches(repo);
    }
  };

  // Handle repository click to switch active repo
  const handleRepoClick = (repoId) => {
    setActiveRepoId(repoId);
    setBranchSearchQuery(''); // Reset search when switching repos
    
    // Initialize normal branch pagination state for this repo if not exists
    if (!branchPage.has(repoId)) {
      setBranchPage(prev => {
        const newMap = new Map(prev);
        newMap.set(repoId, 1);
        return newMap;
      });
    }
    
    if (!branchPerPage.has(repoId)) {
      setBranchPerPage(prev => {
        const newMap = new Map(prev);
        newMap.set(repoId, 100);
        return newMap;
      });
    }
    
    // Initialize search pagination state for this repo if not exists
    if (!branchSearchPage.has(repoId)) {
      setBranchSearchPage(prev => {
        const newMap = new Map(prev);
        newMap.set(repoId, 1);
        return newMap;
      });
    }
    
    if (!branchSearchPerPage.has(repoId)) {
      setBranchSearchPerPage(prev => {
        const newMap = new Map(prev);
        newMap.set(repoId, 100);
        return newMap;
      });
    }
    
    const repo = selectedRepos.find(r => r.repository_id === repoId);
    if (!repo) return;
    
    // Fetch branches if not already loaded
    const currentBranches = branches.get(repoId);
    if (!currentBranches || currentBranches.branches.length === 0) {
      const per_page = branchPerPage.get(repoId) || 100;
      fetchBranches(repo, 1, per_page);
    }
  };

  // Handle branch pagination
  const handleBranchPageChange = (direction) => {
    if (!activeRepoId) return;
    
    const pagination = branchPagination.get(activeRepoId);
    if (!pagination) return;
    
    let newPage;
    if (direction === 'next' && pagination.has_next) {
      newPage = pagination.page + 1;
    } else if (direction === 'prev' && pagination.has_previous) {
      newPage = pagination.page - 1;
    } else {
      return;
    }
    
    // Additional validation - ensure newPage is valid
    if (newPage < 1) {
      return;
    }
    
    const repo = selectedRepos.find(r => r.repository_id === activeRepoId);
    if (repo) {
      const per_page = branchPerPage.get(activeRepoId) || 100;
      setBranchPage(prev => {
        const newMap = new Map(prev);
        newMap.set(activeRepoId, newPage);
        return newMap;
      });
      fetchBranches(repo, newPage, per_page);
    }
  };

  const handleBranchPerPageChange = (perPage) => {
    if (!activeRepoId) return;
    
    setBranchPerPage(prev => {
      const newMap = new Map(prev);
      newMap.set(activeRepoId, Number(perPage));
      return newMap;
    });
    
    // Reset to page 1 when changing per_page
    setBranchPage(prev => {
      const newMap = new Map(prev);
      newMap.set(activeRepoId, 1);
      return newMap;
    });
    
    const repo = selectedRepos.find(r => r.repository_id === activeRepoId);
    if (repo) {
      fetchBranches(repo, 1, Number(perPage));
    }
  };

  // Handle branch search pagination
  const handleBranchSearchPageChange = (direction) => {
    if (!activeRepoId) return;
    
    const pagination = branchSearchPagination.get(activeRepoId);
    if (!pagination) return;
    
    let newPage;
    if (direction === 'next' && pagination.has_next) {
      newPage = pagination.page + 1;
    } else if (direction === 'prev' && pagination.has_previous) {
      newPage = pagination.page - 1;
    } else {
      return;
    }
    
    // Additional validation - ensure newPage is valid
    if (newPage < 1) {
      return;
    }
    
    setBranchSearchPage(prev => {
      const newMap = new Map(prev);
      newMap.set(activeRepoId, newPage);
      return newMap;
    });
  };

  const handleBranchSearchPerPageChange = (perPage) => {
    if (!activeRepoId) return;
    
    setBranchSearchPerPage(prev => {
      const newMap = new Map(prev);
      newMap.set(activeRepoId, Number(perPage));
      return newMap;
    });
    
    // Reset to page 1 when changing per_page
    setBranchSearchPage(prev => {
      const newMap = new Map(prev);
      newMap.set(activeRepoId, 1);
      return newMap;
    });
  };


  // Schedule configuration helpers (copied from ScanFlowModal)
  const convertIntervalToHours = (interval) => {
    const match = interval.match(/^(\d+)([mhdw])$/);
    if (!match) return 1;

    const value = parseInt(match[1]);
    const unit = match[2];

    switch (unit) {
      case 'm': return value / 60;
      case 'h': return value;
      case 'd': return value * 24;
      case 'w': return value * 24 * 7;
      default: return 1;
    }
  };

  const getCurrentHourInTimezone = (targetTimezone = timezone) => {
    const now = new Date();

    if (!targetTimezone) {
      return now.getHours();
    }

    if (targetTimezone.startsWith('UTC')) {
      const offset = targetTimezone.replace('UTC', '');
      const hours = parseInt(offset);
      const minutes = offset.includes(':') ? parseInt(offset.split(':')[1]) : 0;

      const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
      const offsetTime = new Date(utcTime.getTime() + (hours * 60 + minutes) * 60000);

      return offsetTime.getHours();
    }

    try {
      const timeInTimezone = new Date(now.toLocaleString('en-US', { timeZone: targetTimezone }));
      return timeInTimezone.getHours();
    } catch (error) {
      console.error('Error getting hour in timezone:', error);
      return now.getHours();
    }
  };

  const getTimezoneDisplayName = (tz) => {
    if (!tz) {
      return 'UTC +00:00';
    }

    if (tz.startsWith('UTC')) {
      return tz;
    }
    if (tz === 'Asia/Kolkata') {
      return 'UTC +05:30';
    }
    return tz;
  };

  const getCurrentTimeInTimezone = () => {
    const now = new Date();

    if (!timezone) {
      return now.toLocaleString('en-US');
    }

    if (timezone.startsWith('UTC')) {
      const offset = timezone.replace('UTC', '');
      const hours = parseInt(offset);
      const minutes = offset.includes(':') ? parseInt(offset.split(':')[1]) : 0;

      const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
      const offsetTime = new Date(utcTime.getTime() + (hours * 60 + minutes) * 60000);

      return offsetTime.toLocaleString('en-US');
    }

    return now.toLocaleString('en-US', { timeZone: timezone });
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showScheduleDropdown && !event.target.closest('.schedule-dropdown')) {
        setShowScheduleDropdown(false);
      }
      if (showTimezoneDropdown && !event.target.closest('.timezone-dropdown')) {
        setShowTimezoneDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showScheduleDropdown, showTimezoneDropdown]);

  // Navigation handlers
  const handleNext = () => {
    if (currentStep === 1) {
      if (selectedRepos.length === 0) {
        showToast('Please select at least one repository', 'error');
        return;
      }
      setCurrentStep(2);
      // Auto-select first repository and fetch branches for all repositories
      if (selectedRepos.length > 0) {
        const firstRepo = selectedRepos[0];
        setActiveRepoId(firstRepo.repository_id);
        
        // Fetch branches for all selected repositories to enable auto-selection
        selectedRepos.forEach(repo => {
          fetchBranches(repo, 1);
        });
      }
    } else if (currentStep === 2) {
      // Check for empty repositories first
      if (emptyRepositories.size > 0) {
        showToast('Empty repositories cannot be onboarded. Please remove them or select repositories with branches.', 'error');
        return;
      }
      
      // Validate that each repo has at least one branch selected
      const hasInvalidRepos = selectedRepos.some(repo => {
        const branches = selectedBranches.get(repo.repository_id) || [];
        return branches.length === 0;
      });
      
      if (hasInvalidRepos) {
        showToast('Please select at least one branch for each repository', 'error');
        return;
      }
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Create repository schedule function (mock)
  const createRepositorySchedule = async (scheduleData) => {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Return mock response
      return {
        Id: `schedule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        statusCode: 200,
        message: 'Schedule created successfully'
      };
    } catch (error) {
      console.error('Error creating repository schedule:', error);
      throw error;
    }
  };

  // Concurrency helper
  const processWithConcurrency = async (items, concurrency, handler) => {
    const results = [];
    let index = 0;
    const workers = new Array(Math.min(concurrency, items.length)).fill(0).map(async () => {
      while (index < items.length) {
        const currentIndex = index++;
        try {
          const r = await handler(items[currentIndex], currentIndex);
          results[currentIndex] = { success: true, result: r };
        } catch (e) {
          results[currentIndex] = { success: false, error: e };
        }
      }
    });
    await Promise.all(workers);
    return results;
  };

  // Final submission handler
  const handleCreateSchedules = async () => {
    setIsCreatingSchedules(true);
    
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mock successful response
      showToast('Repositories added successfully!', 'success');
      
      // Store repository info for background scheduling
      const repoInfo = {
        selectedRepos: [...selectedRepos],
        selectedBranches: new Map(selectedBranches),
        repeatInterval,
        customTime,
        startTime,
        timezone
      };
      
      navigate('/integration/github');
      
      scheduleRepositoriesInBackground(repoInfo);
      
    } catch (error) {
      console.error('Error in handleCreateSchedules:', error);
      showToast('Failed to create schedules', 'error');
    } finally {
      setIsCreatingSchedules(false);
    }
  };

  // Background scheduling function (mock)
  const scheduleRepositoriesInBackground = async (repoInfo) => {
    try {
      // Wait for repositories to be processed
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock internal repositories
      const mockInternalRepos = repoInfo.selectedRepos.map((repo, idx) => ({
        repository_id: `internal-repo-${idx + 1}`,
        id: `internal-repo-${idx + 1}`,
        name: repo.name,
        owner: repo.owner
      }));

      if (Array.isArray(mockInternalRepos) && mockInternalRepos.length > 0) {
        const repoMapping = {};
        
        repoInfo.selectedRepos.forEach((selectedRepo, idx) => {
          repoMapping[selectedRepo.repository_id] = mockInternalRepos[idx].repository_id;
        });

        // Step 3: Create schedules for each repo+branch combination
        const scheduleRequests = [];
        
        repoInfo.selectedRepos.forEach((repo) => {
          const internalId = repoMapping[repo.repository_id];
          if (!internalId) return;
          
          const repoBranches = repoInfo.selectedBranches.get(repo.repository_id) || [];
          
          repoBranches.forEach(branchName => {
            const intervalString = repoInfo.repeatInterval === 'custom' ? repoInfo.customTime : repoInfo.repeatInterval;
            const scheduleData = {
              startHour: repoInfo.startTime === 'now' ? 0 : getCurrentHourInTimezone(),
              repeatIntervalH: convertIntervalToHours(intervalString),
              repositoryID: internalId,
              timezone: repoInfo.timezone,
              branch: branchName
            };
            scheduleRequests.push({ 
              scheduleData, 
              repoName: repo.full_name || repo.name || repo.repo_name || `Repository ${repo.repository_id}`, 
              branchName 
            });
          });
        });

        if (scheduleRequests.length > 0) {
          const results = await processWithConcurrency(scheduleRequests, 8, async (req) => {
            return await createRepositorySchedule(req.scheduleData);
          });

          results.forEach((r, idx) => {
            const { repoName, branchName } = scheduleRequests[idx];
            if (r.success && r.result && r.result.Id) {
              showToast(`Scan schedule created for ${repoName}`, 'success');
            } else if (!r.success) {
              const err = r.error;
              const errorMessage = err?.message || `Failed to schedule ${repoName}:${branchName}`;
              showToast(errorMessage, 'error');
            }
          });
        }
      }
    } catch (error) {
      console.error('Background scheduling error:', error);
      showToast('Failed to configure scan schedules. Please try again from repository settings.', 'error');
    }
  };

  // Pagination handlers for normal mode
  const handlePrevPage = () => {
    if (!isSearchMode && pagination.has_previous && pagination.page > 1) {
      fetchRepositories(pagination.page - 1, pagination.per_page);
    }
  };

  const handleNextPage = () => {
    if (!isSearchMode && pagination.has_next && pagination.page < pagination.last_page) {
      fetchRepositories(pagination.page + 1, pagination.per_page);
    }
  };

  const handlePerPageChange = (perPage) => {
    if (!isSearchMode) {
      fetchRepositories(1, perPage);
    }
  };

  // Search pagination handlers
  const handleSearchPrevPage = () => {
    if (searchPagination?.has_previous && searchPage > 1) {
      setSearchPage(searchPage - 1);
    }
  };

  const handleSearchNextPage = () => {
    if (searchPagination?.has_next && searchPage < searchPagination?.last_page) {
      setSearchPage(searchPage + 1);
    }
  };

  const handleSearchPerPageChange = (perPage) => {
    setSearchPerPage(Number(perPage));
    setSearchPage(1);
  };

  // Calculate total branch selections
  const totalBranchSelections = useMemo(() => {
    let total = 0;
    selectedBranches.forEach(branches => {
      total += branches.length;
    });
    return total;
  }, [selectedBranches]);

  return (
    <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
      <div className="mx-auto">
        {/* Breadcrumb + Page Title */}
        <div className="mb-8 space-y-2">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/integration/github">Repositories</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Onboard Repositories</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <h1 className="text-black text-2xl md:text-3xl font-bold">Onboard Repositories</h1>
        </div>

        {/* Step Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  currentStep >= step
                    ? 'bg-[#44087D] border-[#44087D] text-white'
                    : 'bg-white border-gray-300 text-gray-500'
                }`}>
                  {currentStep > step ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-medium">{step}</span>
                  )}
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  currentStep >= step ? 'text-[#44087D]' : 'text-gray-500'
                }`}>
                  {step === 1 && 'Select Repositories'}
                  {step === 2 && 'Choose Branches'}
                  {step === 3 && 'Schedule Scans'}
                </span>
                {step < 3 && (
                  <div className={`w-8 h-0.5 mx-4 ${
                    currentStep > step ? 'bg-[#44087D]' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <Card className="bg-white border border-gray-300 shadow-none">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900">
              {currentStep === 1 && 'Select Repositories to Onboard'}
              {currentStep === 2 && 'Choose Branches for Each Repository'}
              {currentStep === 3 && 'Configure Scan Schedule'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Step 1: Repository Selection */}
            {currentStep === 1 && (
              <div className="space-y-4">
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search repositories..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Counter */}
                <div className="flex items-center justify-end pb-2 border-b">
                  {selectedRepos.length > 0 && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {selectedRepos.length} repo{selectedRepos.length > 1 ? "s" : ""} selected
                    </span>
                  )}
                </div>

                {/* Repository List */}
                <div className="max-h-96 overflow-y-auto border rounded-md">
                  {(isLoadingRepos || isSearchLoading) ? (
                    <div className="divide-y">
                      {[...Array(8)].map((_, index) => (
                        <RepositoryShimmer key={index} />
                      ))}
                    </div>
                  ) : searchError ? (
                    <div className="flex items-center justify-center h-32 text-red-500">
                      <p className="text-center">
                        {searchError}
                      </p>
                    </div>
                  ) : displayedRepositories.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-gray-500">
                      <p className="text-center">
                        {searchQuery.trim().length >= 2 ? (
                          searchError ? searchError : 'No repositories found matching your search.'
                        ) : searchQuery.trim() ? (
                          'Type at least 2 characters to search globally'
                        ) : (
                          'No repositories available.'
                        )}
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {displayedRepositories.map((repo) => (
                        <label
                          key={repo.repository_id}
                          className={`flex items-center gap-3 p-4 cursor-pointer transition-colors ${
                            selectedRepos.some((r) => r.repository_id === repo.repository_id)
                              ? "bg-purple-50"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedRepos.some((r) => r.repository_id === repo.repository_id)}
                            onChange={() => handleSelectRepo(repo)}
                            className="h-4 w-4 text-[#44087D] border-gray-300 rounded focus:ring-[#44087D] focus:ring-2"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {repo.name || repo.full_name}
                            </p>
                            {repo.description && (
                              <p className="text-xs text-gray-500 truncate mt-1">
                                {repo.description}
                              </p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pagination */}
                <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 sm:gap-0 pt-4 border-t">
                  {isSearchMode ? (
                    // Search mode pagination
                    <>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSearchPrevPage}
                          disabled={!searchPagination?.has_previous}
                          className="flex items-center gap-1"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>
                        <span className="text-sm text-gray-600 mx-2">
                          Page {searchPagination?.page || 1} of {searchPagination?.last_page || 1}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSearchNextPage}
                          disabled={!searchPagination?.has_next}
                          className="flex items-center gap-1"
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">Per page:</label>
                        <select
                          value={searchPerPage}
                          onChange={(e) => handleSearchPerPageChange(e.target.value)}
                          className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#44087D] focus:border-transparent"
                        >
                          {[5, 10, 20, 30, 50].map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  ) : (
                    // Normal mode pagination
                    <>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handlePrevPage}
                          disabled={!pagination.has_previous}
                          className="flex items-center gap-1"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>
                        <span className="text-sm text-gray-600 mx-2">
                          Page {pagination.page} of {pagination.last_page}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleNextPage}
                          disabled={!pagination.has_next}
                          className="flex items-center gap-1"
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">Per page:</label>
                        <select
                          value={pagination.per_page}
                          onChange={(e) => handlePerPageChange(Number(e.target.value))}
                          className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#44087D] focus:border-transparent"
                        >
                          {[5, 10, 20, 50, 100].map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Branch Selection */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-gray-600">
                    Select branches for each repository. At least one branch must be selected per repository.
                  </div>
                </div>
                
                {/* Master-Detail Split Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                  {/* Left Panel: Repository List */}
                  <div className="lg:col-span-4 border-r border-gray-200 pr-4">
                    <div className="flex items-center gap-2 mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Selected Repositories ({selectedRepos.length})
                      </h3>
                    </div>
                    
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {selectedRepos.map((repo) => {
                        const selectedRepoBranches = selectedBranches.get(repo.repository_id) || [];
                        const isActive = activeRepoId === repo.repository_id;
                        const isEmpty = emptyRepositories.has(repo.repository_id);
                        
                        return (
                          <div
                            key={repo.repository_id}
                            className={`p-3 rounded-lg transition-colors ${
                              isEmpty 
                                ? 'bg-red-50 border border-red-200'
                                : isActive 
                                  ? 'bg-[#44087D] text-white cursor-pointer' 
                                  : 'bg-gray-50 hover:bg-gray-100 text-gray-900 cursor-pointer'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div 
                                className="flex-1 min-w-0 cursor-pointer"
                                onClick={() => !isEmpty && handleRepoClick(repo.repository_id)}
                              >
                                <p className="text-sm font-medium truncate">
                                  {repo.full_name || repo.name || repo.repo_name || repo.title || `Repository ${repo.id}`}
                                </p>
                                <p className={`text-xs mt-1 ${
                                  isEmpty ? 'text-red-600' : isActive ? 'text-purple-100' : 'text-gray-500'
                                }`}>
                                  {isEmpty 
                                    ? 'Empty Repository' 
                                    : `${selectedRepoBranches.length} branch${selectedRepoBranches.length > 1 ? 'es' : ''} selected`
                                  }
                                </p>
                              </div>
                              {isEmpty ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedRepos(prev => prev.filter(r => r.repository_id !== repo.repository_id));
                                    setEmptyRepositories(prev => {
                                      const newSet = new Set(prev);
                                      newSet.delete(repo.repository_id);
                                      return newSet;
                                    });
                                  }}
                                  className="flex-shrink-0 p-2 hover:bg-red-200 rounded-md transition-colors"
                                  title="Remove this repository"
                                >
                                  <svg 
                                    className="w-4 h-4 text-red-600" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
                                    />
                                  </svg>
                                </button>
                              ) : isActive && (
                                <ChevronRight className="w-4 h-4 text-white" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Right Panel: Branch List with Pagination */}
                  <div className="lg:col-span-8">
                    {activeRepoId ? (
                      <>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-gray-900">Branches</h3>
                            {emptyRepositories.has(activeRepoId) && (
                              <span className="text-xs bg-red-200 text-red-800 px-2 py-1 rounded">
                                No Branches Found
                              </span>
                            )}
                          </div>
                          
                        </div>
                        
                        {/* Branch Search Bar */}
                        <div className="relative mb-4">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            type="text"
                            placeholder="Search branches..."
                            value={branchSearchQuery}
                            onChange={(e) => setBranchSearchQuery(e.target.value)}
                            className="pl-10"
                          />
                          {branchSearchQuery && (
                            <button
                              onClick={() => setBranchSearchQuery('')}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                        
                        {/* Branch List */}
                        <div className="branch-list-container space-y-2 max-h-96 overflow-y-auto border rounded-lg p-4">
                          {(() => {
                            const repoBranches = branches.get(activeRepoId);
                            const selectedRepoBranches = selectedBranches.get(activeRepoId) || [];
                            const isSearchMode = isBranchSearchMode.get(activeRepoId) || false;
                            const searchResults = branchSearchResults.get(activeRepoId) || [];
                            
                            // Determine which branches to display
                            const displayBranches = isSearchMode ? searchResults : (repoBranches?.branches || []);
                            
                            if (repoBranches?.loading || isBranchSearchLoading) {
                              return (
                                <div className="space-y-2">
                                  {[...Array(3)].map((_, index) => (
                                    <BranchShimmer key={index} />
                                  ))}
                                </div>
                              );
                            }
                            
                            if (displayBranches.length > 0) {
                              // Sort branches so selected ones appear at the top
                              const sortedBranches = [...displayBranches].sort((a, b) => {
                                const aSelected = selectedRepoBranches.includes(a.name);
                                const bSelected = selectedRepoBranches.includes(b.name);
                                
                                if (aSelected && !bSelected) return -1;
                                if (!aSelected && bSelected) return 1;
                                return 0;
                              });
                              
                              return (
                                <div className="space-y-2">
                                  {isSearchMode && searchResults.length > 0 && (
                                    <div className="text-xs text-gray-500 mb-2">
                                      {(() => {
                                        const searchPagination = branchSearchPagination.get(activeRepoId);
                                        if (searchPagination) {
                                          const start = (searchPagination.page - 1) * searchPagination.per_page + 1;
                                          const end = Math.min(searchPagination.page * searchPagination.per_page, searchResults.length);
                                          return `Showing ${start}-${end} results for "${branchSearchQuery}"`;
                                        }
                                        return `Found ${searchResults.length} branch${searchResults.length > 1 ? 'es' : ''} matching "${branchSearchQuery}"`;
                                      })()}
                                    </div>
                                  )}
                                  {sortedBranches.map((branch, index) => (
                                    <label
                                      key={`${activeRepoId}-${branch.name}-${index}`}
                                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selectedRepoBranches.includes(branch.name)}
                                        onChange={() => handleSelectBranch(activeRepoId, branch.name)}
                                        className="h-4 w-4 text-[#44087D] border-gray-300 rounded focus:ring-[#44087D] focus:ring-2"
                                      />
                                      <span className="text-sm text-gray-900">{branch.name}</span>
                                      {branch.protected && (
                                        <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                          Protected
                                        </span>
                                      )}
                                    </label>
                                  ))}
                                </div>
                              );
                            }
                            
                            return (
                              <div className="text-sm text-gray-500 py-4 text-center">
                                {isSearchMode ? (
                                  `No branches found matching "${branchSearchQuery}"`
                                ) : emptyRepositories.has(activeRepoId) ? (
                                  'This repository has no branches and cannot be onboarded.'
                                ) : (
                                  'No branches found for this repository.'
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Pagination Controls */}
                        {(() => {
                          const isSearchMode = isBranchSearchMode.get(activeRepoId) || false;
                          const pagination = isSearchMode 
                            ? branchSearchPagination.get(activeRepoId) 
                            : branchPagination.get(activeRepoId);
                          
                          // Show pagination if available
                          if (!pagination) return null;
                          
                          if (isSearchMode) {
                            // Search mode pagination
                            const currentPerPage = branchSearchPerPage.get(activeRepoId) || 100;
                            
                            return (
                              <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 sm:gap-0 mt-4 pt-4 border-t">
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleBranchSearchPageChange('prev')}
                                    disabled={!pagination.has_previous}
                                    className="flex items-center gap-1"
                                  >
                                    <ChevronLeft className="h-4 w-4" />
                                    Previous
                                  </Button>
                                  <span className="text-sm text-gray-600 mx-2">
                                    Page {pagination.page} of {pagination.last_page || 1}
                                  </span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleBranchSearchPageChange('next')}
                                    disabled={!pagination.has_next}
                                    className="flex items-center gap-1"
                                  >
                                    Next
                                    <ChevronRight className="h-4 w-4" />
                                  </Button>
                                </div>

                                <div className="flex items-center gap-2">
                                  <label className="text-sm text-gray-600">Per page:</label>
                                  <select
                                    value={currentPerPage}
                                    onChange={(e) => handleBranchSearchPerPageChange(e.target.value)}
                                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#44087D] focus:border-transparent"
                                  >
                                    {[10, 20, 50, 100].map((n) => (
                                      <option key={n} value={n}>
                                        {n}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            );
                          } else {
                            // Normal mode pagination
                            const currentPerPage = branchPerPage.get(activeRepoId) || 100;
                            
                            return (
                              <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 sm:gap-0 mt-4 pt-4 border-t">
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleBranchPageChange('prev')}
                                    disabled={!pagination.has_previous}
                                    className="flex items-center gap-1"
                                  >
                                    <ChevronLeft className="h-4 w-4" />
                                    Previous
                                  </Button>
                                  <span className="text-sm text-gray-600 mx-2">
                                    Page {pagination.page} of {Math.max(pagination.last_page || 1, pagination.page || 1)}
                                  </span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleBranchPageChange('next')}
                                    disabled={!pagination.has_next}
                                    className="flex items-center gap-1"
                                  >
                                    Next
                                    <ChevronRight className="h-4 w-4" />
                                  </Button>
                                </div>

                                <div className="flex items-center gap-2">
                                  <label className="text-sm text-gray-600">Per page:</label>
                                  <select
                                    value={currentPerPage}
                                    onChange={(e) => handleBranchPerPageChange(e.target.value)}
                                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#44087D] focus:border-transparent"
                                  >
                                    {[10, 20, 50, 100].map((n) => (
                                      <option key={n} value={n}>
                                        {n}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            );
                          }
                        })()}
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-64 text-gray-500">
                        <div className="text-center">
                          <p>Select a repository to view its branches</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Schedule Configuration */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="text-sm text-gray-600 mb-4">
                  Configure scan schedule for {selectedRepos.length} repositories with {totalBranchSelections} total branch selections.
                </div>

                {/* Collapsible Summary Accordion */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg">
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleRepoExpansion('summary')}
                  >
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Selected Repositories & Branches - Summary
                      </h3>
                      <span className="text-sm text-gray-500">
                        ({selectedRepos.length} repos, {totalBranchSelections} branches)
                      </span>
                    </div>
                    {branches.get('summary')?.expanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </div>
                  
                  {branches.get('summary')?.expanded && (
                    <div className="border-t p-4 bg-white">
                      {isCreatingSchedules ? (
                        <div className="space-y-3">
                          {[...Array(selectedRepos.length)].map((_, index) => (
                            <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                              <div className="flex items-center gap-3">
                                <ShimmerCard className="w-4 h-4 rounded" />
                                <div className="flex-1">
                                  <ShimmerText width="w-1/2" height="h-4" />
                                  <ShimmerText width="w-1/4" height="h-3" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                          {selectedRepos.map((repo) => {
                            const selectedRepoBranches = selectedBranches.get(repo.repository_id) || [];
                            
                            return (
                              <div key={repo.repository_id} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="flex-1">
                                    <span className="text-sm font-medium text-gray-900">
                                      {repo.full_name || repo.name || repo.repo_name || repo.title || `Repository ${repo.repository_id}`}
                                    </span>
                                    <div className="text-xs text-gray-500">
                                      {selectedRepoBranches.length} branch{selectedRepoBranches.length > 1 ? 'es' : ''} selected
                                    </div>
                                  </div>
                                </div>
                                
                                {selectedRepoBranches.length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {selectedRepoBranches.map((branchName) => (
                                      <span
                                        key={branchName}
                                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                                      >
                                        {branchName}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Schedule Configuration Panel */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Schedule Configuration</h3>
                  </div>

                {/* Timezone Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                  <div className="relative timezone-dropdown">
                    <button
                      type="button"
                      onClick={() => setShowTimezoneDropdown(!showTimezoneDropdown)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <span className="text-gray-900">{getTimezoneDisplayName(timezone)}</span>
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {showTimezoneDropdown && (
                      <div className="absolute z-20 w-80 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg timezone-dropdown">
                        <div className="p-4">
                          <h4 className="text-white text-sm font-medium mb-3">Select Timezone</h4>

                          <div className="mb-3 p-2 bg-gray-700 rounded">
                            <button
                              onClick={() => {
                                setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
                                setShowTimezoneDropdown(false);
                              }}
                              className={`w-full text-left px-2 py-1 rounded text-sm transition-colors ${
                                timezone === Intl.DateTimeFormat().resolvedOptions().timeZone
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-300 hover:bg-gray-600'
                              }`}
                            >
                              {getTimezoneDisplayName(Intl.DateTimeFormat().resolvedOptions().timeZone)} (System Default)
                            </button>
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            {[
                              'UTC +00:00', 'UTC +01:00', 'UTC +02:00', 'UTC +03:00', 'UTC +04:00',
                              'UTC +05:00', 'UTC +05:30', 'UTC +06:00', 'UTC +06:30', 'UTC +07:00',
                              'UTC +08:00', 'UTC +08:30', 'UTC +09:00', 'UTC +10:00', 'UTC +11:00',
                              'UTC +12:00'
                            ].map((tz) => (
                              <button
                                key={tz}
                                onClick={() => {
                                  setTimezone(tz);
                                  setShowTimezoneDropdown(false);
                                }}
                                className={`px-2 py-1 rounded text-sm font-medium transition-colors ${
                                  timezone === tz
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                }`}
                              >
                                {tz}
                              </button>
                            ))}
                          </div>

                          <div className="mt-3 pt-3 border-t border-gray-600">
                            <h5 className="text-white text-sm font-medium mb-2">Negative Offsets</h5>
                            <div className="grid grid-cols-2 gap-2">
                              {[
                                'UTC -01:00', 'UTC -02:00', 'UTC -03:00', 'UTC -04:00', 'UTC -05:00',
                                'UTC -06:00', 'UTC -07:00', 'UTC -08:00', 'UTC -09:00', 'UTC -10:00',
                                'UTC -11:00', 'UTC -12:00'
                              ].map((tz) => (
                                <button
                                  key={tz}
                                  onClick={() => {
                                    setTimezone(tz);
                                    setShowTimezoneDropdown(false);
                                  }}
                                  className={`px-2 py-1 rounded text-sm font-medium transition-colors ${
                                    timezone === tz
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                  }`}
                                >
                                  {tz}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Start Time Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                  <div className="flex gap-2">
                    <label className="flex items-center px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 flex-1">
                      <input
                        type="radio"
                        name="startTime"
                        value="now"
                        checked={startTime === 'now'}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="w-4 h-4 text-blue-600 mr-2"
                      />
                      <div>
                        <div className="text-sm font-medium">Now</div>
                        <div className="text-xs text-gray-500">Start immediately</div>
                      </div>
                    </label>
                    <label className="flex items-center px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 flex-1">
                      <input
                        type="radio"
                        name="startTime"
                        value="hour"
                        checked={startTime === 'hour'}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="w-4 h-4 text-blue-600 mr-2"
                      />
                      <div>
                        <div className="text-sm font-medium">At {getCurrentHourInTimezone()}:00</div>
                        <div className="text-xs text-gray-500">Start at current hour</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Repeat Interval Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Repeat Every</label>
                  <div className="relative schedule-dropdown">
                    <button
                      type="button"
                      onClick={() => setShowScheduleDropdown(!showScheduleDropdown)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-gray-900">{repeatInterval === 'custom' ? customTime : `Every ${repeatInterval}`}</span>
                      </div>
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {showScheduleDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg schedule-dropdown">
                        <div className="p-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <h4 className="text-white text-sm font-medium mb-3">Common Intervals</h4>
                              <div className="space-y-2">
                                {[
                                  { label: 'Every 1 hour', value: '1h' },
                                  { label: 'Every 6 hours', value: '6h' },
                                  { label: 'Every 1 day', value: '1d' },
                                  { label: 'Every 3 days', value: '3d' },
                                  { label: 'Every 1 week', value: '1w' },
                                  { label: 'Every 1 month', value: '1mo' }
                                ].map((option) => (
                                  <button
                                    key={option.value}
                                    onClick={() => {
                                      setRepeatInterval(option.value);
                                      setShowScheduleDropdown(false);
                                    }}
                                    className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                                      repeatInterval === option.value
                                        ? 'bg-blue-600 text-white'
                                        : 'text-gray-300 hover:bg-gray-700'
                                    }`}
                                  >
                                    {option.label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div>
                              <h4 className="text-white text-sm font-medium mb-3">Other Intervals</h4>
                              <div className="grid grid-cols-2 gap-2">
                                {[
                                  { label: '3h', value: '3h' },
                                  { label: '4d', value: '4d' },
                                  { label: '6w', value: '6w' },
                                  { label: '12h', value: '12h' },
                                  { label: '10d', value: '10d' },
                                  { label: '2w', value: '2w' },
                                  { label: '2mo', value: '2mo' },
                                  { label: 'today', value: '1d' }
                                ].map((option) => (
                                  <button
                                    key={option.value}
                                    onClick={() => {
                                      setRepeatInterval(option.value);
                                      setShowScheduleDropdown(false);
                                    }}
                                    className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                                      repeatInterval === option.value
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                    }`}
                                  >
                                    {option.value}
                                  </button>
                                ))}
                              </div>

                              <div className="mt-4">
                                <h5 className="text-white text-sm font-medium mb-2">Custom</h5>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={customTime}
                                    onChange={(e) => setCustomTime(e.target.value)}
                                    placeholder="1h, 30m, 2d"
                                    className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm placeholder-gray-400"
                                  />
                                  <button
                                    onClick={() => {
                                      setRepeatInterval('custom');
                                      setShowScheduleDropdown(false);
                                    }}
                                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                                  >
                                    Set
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Schedule Preview */}
                <div className="bg-white border border-purple-200 rounded-md p-3">
                  <div className="text-sm text-purple-800">
                    <strong>Preview:</strong> Next scan at {startTime === 'now' ? getCurrentTimeInTimezone() : `${getCurrentHourInTimezone()}:00`}, repeating every 1hr
                  </div>
                </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          
          <Button
            variant="outline"
            onClick={handleBack}
            className={`flex items-center gap-2 ${currentStep === 1 ? 'invisible' : ''}`}
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>

          <div className="flex gap-2">
            {currentStep < 3 ? (
              <Button
                onClick={handleNext}
                disabled={
                  (currentStep === 1 && selectedRepos.length === 0) ||
                  (currentStep === 2 && selectedRepos.some(repo => (selectedBranches.get(repo.repository_id) || []).length === 0))
                }
                className="bg-[#44087D] hover:bg-[#5a0a9a] text-white flex items-center gap-2"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleCreateSchedules}
                disabled={isCreatingSchedules}
                className="bg-[#44087D] hover:bg-[#5a0a9a] text-white flex items-center gap-2"
              >
                {isCreatingSchedules ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating Schedules...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Create Schedules
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
      </div>
  );
};

export default OnboardingPage;
